/* eslint-disable jsx-a11y/label-has-associated-control, no-unused-vars */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import ManualQuizCreator from '../components/ManualQuizCreator';

const QuizUploadPage = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [file, setFile] = useState(null);
  const [answerFile, setAnswerFile] = useState(null);
  
  const [creationMode, setCreationMode] = useState(() => {
    // Load creation mode from localStorage
    const savedMode = localStorage.getItem('quiz_upload_draft_creation_mode');
    console.log('🔍 [QuizUpload] Loading creation mode from localStorage:', savedMode);
    return savedMode || 'upload';
  });
  
  // accept parsedQuestions from navigation state OR load from localStorage draft
  const [questions, setQuestions] = useState(() => {
    // First check navigation state
    const parsedQuestions = location.state?.parsedQuestions;
    if (parsedQuestions && parsedQuestions.length > 0) {
      return parsedQuestions.map(q => ({ 
        text: q.text || '', 
        options: Array.isArray(q.options) ? q.options : ['', ''], 
        answer: q.answer || '' 
      }));
    }
    
    // Then check localStorage draft
    const draftKey = `quiz_upload_draft_questions`;
    const saved = localStorage.getItem(draftKey);
    console.log('🔍 [QuizUpload] Loading questions from localStorage:', saved ? JSON.parse(saved).length + ' questions' : 'none');
    if (saved) {
      const draft = JSON.parse(saved);
      return draft.map(q => q._original || q); // Restore original format if available
    }
    
    return [];
  });
  
  const [title, setTitle] = useState('');
  const [courseId, setCourseId] = useState('');
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [studentType, setStudentType] = useState('');
  const [questionImages, setQuestionImages] = useState({}); // Store images for each question
  const [uploadingImage, setUploadingImage] = useState({}); // Track uploading state per question
  const navigate = useNavigate();

  // Save creation mode to localStorage
  useEffect(() => {
    console.log('💾 [QuizUpload] Saving creation mode to localStorage:', creationMode);
    localStorage.setItem('quiz_upload_draft_creation_mode', creationMode);
  }, [creationMode]);

  // Save questions to localStorage as draft
  useEffect(() => {
    console.log('📝 [QuizUpload] questions changed:', {
      mode: creationMode,
      count: questions.length
    });
    
    if (creationMode === 'manual') {
      const draftKey = `quiz_upload_draft_questions`;
      console.log('💾 [QuizUpload] Saving questions to localStorage:', questions.length, 'questions');
      localStorage.setItem(draftKey, JSON.stringify(questions));
      console.log('✅ [QuizUpload] Draft saved successfully');
    } else {
      console.log('⏭️ [QuizUpload] Skipping save - mode is:', creationMode);
    }
  }, [questions, creationMode]);

  // Stable callback for ManualQuizCreator
  const handleQuestionsCreated = useCallback((manualQuestions) => {
    console.log('📥📥📥 [QuizUpload] PARENT RECEIVED questions from ManualQuizCreator:', manualQuestions.length, new Date().toLocaleTimeString());
    console.log('[QuizUpload] Questions data:', manualQuestions);
    setQuestions(manualQuestions);
  }, []);

  useEffect(() => {
    api.get('/courses', { params: { limit: 1000 } })
      .then(res => setCourses(res.data.data || res.data))
      .catch(console.error);
  }, []);

  const handleUpload = async () => {
    if (!file) return alert('Select a file');
    setIsLoading(true);
    const formData = new FormData();
    formData.append('attachment', file);
    try {
      const res = await api.post('/questions/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      
      // Backend now returns { questions: [...], fileUrl: "...", filePath: "..." }
      const parsedQuestions = res.data.questions || res.data || [];
      
      setQuestions(parsedQuestions.map(q => ({ 
        text: q.text || '', 
        options: Array.isArray(q.options) ? q.options : ['', ''], 
        answer: q.answer || '',
        parseError: q.parseError || false,
        errorMessage: q.errorMessage || null,
        originalBlock: q.originalBlock || null
      })));
      
      // Count and display parse errors
      const errorCount = parsedQuestions.filter(q => q.parseError).length;
      const successCount = parsedQuestions.length - errorCount;
      
      // Check for box characters (encoding issues)
      const hasBoxCharacters = parsedQuestions.some(q => 
        q.text && (q.text.includes('□') || q.text.includes('�') || q.text.includes('[?]'))
      );
      
      console.log(`✅ Loaded ${parsedQuestions.length} questions from PDF (${successCount} successful, ${errorCount} with errors)`);
      
      if (hasBoxCharacters) {
        alert(`⚠️ Encoding Issue Detected!\n\nThe PDF contains mathematical symbols that couldn't be extracted properly (showing as boxes □ or [?]).\n\n✅ SOLUTION:\n1. Review questions with boxes or [?] symbols\n2. Screenshot the original math expression from the PDF\n3. Paste the screenshot directly into that question card (Ctrl+V)\n4. The image will preserve the exact notation for students`);
      } else if (errorCount > 0) {
        alert(`Parsing completed with ${errorCount} error(s) out of ${parsedQuestions.length} questions.\n\nQuestions with errors are marked in red and need manual review before saving.`);
      }
    } catch (err) {
      console.error('Parse error:', err);
      alert(err.response?.data?.error || 'Parse failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadAnswers = async () => {
    if (!answerFile) return alert('Select an answer file');
    setIsLoading(true);
    const form = new FormData();
    form.append('attachment', answerFile);
    try {
      const res = await api.post('/questions/parse-answers', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const answersData = res.data; // Array of { questionNumber, answer }
      
      // Ensure answersData is an array
      if (!Array.isArray(answersData)) {
        console.error('Invalid response format: expected an array of answers', res.data);
        alert('Invalid answer file format. Please ensure your answer file contains numbered answers (1. A, 2. B, etc.)');
        return;
      }
      
      if (answersData.length === 0) {
        alert('No answers found in the file. Please check the format of your answer file.');
        return;
      }
      
      // Create a map of question number to answer
      const answerMap = {};
      answersData.forEach(item => {
        if (item.questionNumber && item.answer) {
          answerMap[item.questionNumber] = item.answer;
        }
      });
      
      console.log('📝 Answer map:', answerMap);
      
      setQuestions(prevQs => prevQs.map((q, idx) => {
        // Ensure q has options array
        if (!q.options || !Array.isArray(q.options)) {
          console.warn(`Question ${idx + 1} has invalid options`, q);
          return { ...q, options: q.options || ['', ''] };
        }
        
        // Match by question number (idx + 1 since questions are 1-indexed)
        const questionNum = idx + 1;
        const answerLetter = answerMap[questionNum];
        
        if (!answerLetter) {
          console.warn(`No answer found for question ${questionNum}`);
          return q;
        }
        
        const optIdx = answerLetter.charCodeAt(0) - 65; // Convert A,B,C,D to 0,1,2,3
        const selectedOption = q.options[optIdx];
        
        if (!selectedOption) {
          console.warn(`Question ${questionNum}: Answer ${answerLetter} doesn't match any option (index ${optIdx})`);
        }
        
        return { 
          ...q, 
          answer: selectedOption || q.answer,
          options: q.options || ['', ''] // Ensure options always exists
        };
      }));
      
      alert(`Successfully parsed ${answersData.length} answers from the file.`);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.error || 'Failed to parse answers';
      if (errorMessage.includes('Unsupported file type')) {
        alert('Unsupported file type. Please upload a PDF, DOC, or DOCX file.');
      } else if (errorMessage.includes('File required')) {
        alert('Please select an answer file to upload.');
      } else {
        alert(`Failed to parse answers: ${errorMessage}\n\nPlease ensure your answer file contains numbered answers in this format:\n1. A\n2. B\n3. C\netc.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!title) return alert('Provide title');
    setIsLoading(true);
    try {
      // Merge questions with their images
      const questionsWithImages = questions.map((q, idx) => ({
        ...q,
        images: questionImages[idx] || []
      }));
      
      const payload = {
        title,
        courseId: courseId ? parseInt(courseId, 10) : null,
        questions: questionsWithImages,
        studentType
      };
      const res = await api.post('/quizzes/from-questions', payload);
      
      // Clear draft from localStorage on successful submission
      localStorage.removeItem('quiz_upload_draft_questions');
      localStorage.removeItem('quiz_upload_draft_creation_mode');
      console.log('🗑️ [QuizUpload] Draft cleared from localStorage');
      
      // Navigate to the edit page of the just created quiz
      navigate(`/teachers/dashboard/quiz/${res.data.id}/edit`);
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating quiz');
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuestion = (idx, text) => {
    const qs = [...questions]; qs[idx].text = text; setQuestions(qs);
  };

  const updateOption = (qIdx, oIdx, value) => {
    const qs = [...questions];
    if (!qs[qIdx].options) qs[qIdx].options = [];
    qs[qIdx].options[oIdx] = value;
    setQuestions(qs);
  };

  const addOption = qIdx => {
    const qs = [...questions]; 
    if (!qs[qIdx].options) qs[qIdx].options = [];
    qs[qIdx].options.push(''); 
    setQuestions(qs);
  };

  const removeOption = (qIdx, oIdx) => {
    const qs = [...questions]; 
    if (!qs[qIdx].options) qs[qIdx].options = [];
    qs[qIdx].options.splice(oIdx, 1); 
    setQuestions(qs);
  };

  const updateAnswer = (qIdx, value) => {
    const qs = [...questions]; qs[qIdx].answer = value; setQuestions(qs);
  };

  // Handle image paste
  const handlePaste = async (e, questionIndex) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = items[i].getAsFile();
        const reader = new FileReader();
        
        reader.onload = async (event) => {
          const base64Image = event.target.result;
          
          // Show uploading indicator
          setUploadingImage(prev => ({ ...prev, [questionIndex]: true }));
          
          try {
            // Upload to Supabase
            console.log('📤 Uploading image to Supabase...');
            const response = await api.post('/images/upload-base64', { 
              base64Image 
            });
            
            if (response.data.success) {
              console.log('✅ Image uploaded:', response.data.url);
              // Store the Supabase URL instead of base64
              setQuestionImages(prev => ({
                ...prev,
                [questionIndex]: [...(prev[questionIndex] || []), response.data.url]
              }));
            }
          } catch (error) {
            console.error('❌ Failed to upload image:', error);
            alert('Failed to upload image. Please try again.');
          } finally {
            // Hide uploading indicator
            setUploadingImage(prev => ({ ...prev, [questionIndex]: false }));
          }
        };
        
        reader.readAsDataURL(blob);
      }
    }
  };

  // Remove image from question
  const removeImage = (questionIndex, imageIndex) => {
    setQuestionImages(prev => ({
      ...prev,
      [questionIndex]: prev[questionIndex].filter((_, idx) => idx !== imageIndex)
    }));
  };

  return (
    <>
      {isLoading && <LoadingScreen message="Processing quiz file..." />}
      <div className="container mx-auto px-4 py-8 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-screen">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-8">
          <h1 className="text-4xl font-extrabold text-indigo-700 mb-8 text-center">Create Quiz</h1>
          
          {/* Mode Selector */}
          <div className="mb-8 flex space-x-2 bg-gray-100 p-2 rounded-xl max-w-md mx-auto">
            <button
              type="button"
              onClick={() => {
                setCreationMode('upload');
                setQuestions([]);
              }}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all ${
                creationMode === 'upload'
                  ? 'bg-white text-indigo-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📤 Upload PDF
            </button>
            <button
              type="button"
              onClick={() => {
                setCreationMode('manual');
                setFile(null);
                setAnswerFile(null);
                setQuestions([]);
              }}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all ${
                creationMode === 'manual'
                  ? 'bg-white text-indigo-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ✍️ Create Manually
            </button>
          </div>

          {/* Upload Mode */}
          {creationMode === 'upload' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex flex-col items-center justify-center gap-4 p-6 bg-gray-50 rounded-xl shadow">
                <label className="font-semibold text-gray-700">Quiz File</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={e => setFile(e.target.files[0])}
                  className="border rounded px-3 py-2 bg-gray-100 w-full"
                />
                <button
                  onClick={handleUpload}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded shadow transition w-full"
                  disabled={courses.length === 0}
                  title={courses.length === 0 ? "You are not assigned to any course" : ""}
                >
                  Parse Questions
                </button>
              </div>
              <div className="flex flex-col items-center justify-center gap-4 p-6 bg-gray-50 rounded-xl shadow">
                <label className="font-semibold text-gray-700">Answer File</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={e => setAnswerFile(e.target.files[0])}
                  className="border rounded px-3 py-2 bg-gray-100 w-full"
                />
                <button
                  onClick={handleUploadAnswers}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded shadow transition w-full"
                  disabled={courses.length === 0}
                  title={courses.length === 0 ? "You are not assigned to any course" : ""}
                >
                  Parse Answers
                </button>
              </div>
            </div>
          )}

          {/* Manual Mode */}
          {creationMode === 'manual' && (
            <div className="mb-8">
              {questions.length > 0 && (
                <div className="mb-4 flex justify-between items-center bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div>
                    <span className="text-sm text-blue-700 font-medium">
                      📝 Draft: {questions.length} question{questions.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Clear all draft questions? This cannot be undone.')) {
                        setQuestions([]);
                        localStorage.removeItem('quiz_upload_draft_questions');
                        console.log('🗑️ Draft cleared manually');
                      }
                    }}
                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    Clear Draft
                  </button>
                </div>
              )}
              <ManualQuizCreator
                onQuestionsCreated={handleQuestionsCreated}
                initialQuestions={questions.map(q => {
                  // Restore original format if available
                  if (q._original) {
                    return q._original;
                  }
                  return q;
                })}
              />
            </div>
          )}

          {questions.length > 0 && (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-2 font-semibold text-gray-700">Quiz Title</label>
                  <input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-2 font-semibold text-gray-700">Course</label>
                  <select
                    value={courseId}
                    onChange={e => setCourseId(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select course</option>
                    {courses.map(c => <option key={c.id} value={c.id}>{c.name || c.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block mb-2 font-semibold text-gray-700">Student Type</label>
                  <select
                    value={studentType}
                    onChange={e => setStudentType(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select student type</option>
                    <option value="shs1">SHS 1</option>
                    <option value="shs2">SHS 2</option>
                    <option value="shs3">SHS 3</option>
                  </select>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-indigo-600 mt-8 mb-4">Questions</h2>
              
              {/* Error Summary Banner */}
              {questions.some(q => q.parseError) && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
                  <div className="flex items-start">
                    <span className="text-2xl mr-3">⚠️</span>
                    <div>
                      <h3 className="text-yellow-800 font-bold text-lg">Parsing Errors Detected</h3>
                      <p className="text-yellow-700 mt-1">
                        {questions.filter(q => q.parseError).length} out of {questions.length} questions had parsing errors.
                        Questions with errors are highlighted in red below. Please review and manually edit them before creating the quiz.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {questions.map((q, i) => (
                <div 
                  key={i} 
                  className={`mb-8 border rounded-xl p-6 shadow ${q.parseError ? 'bg-red-50 border-red-300' : 'bg-gray-50'}`}
                >
                  {q.parseError && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded">
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">⚠️</span>
                        <div>
                          <p className="font-bold">Parsing Error Detected</p>
                          <p className="text-sm mt-1">
                            This question failed to parse correctly. The original text is shown below as a preview.
                            <strong> Please manually edit the question text and options.</strong>
                          </p>
                          {q.errorMessage && (
                            <p className="text-xs mt-2 font-mono bg-red-200 p-2 rounded">
                              Error: {q.errorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={`font-semibold mb-2 text-lg ${q.parseError ? 'text-red-800' : 'text-gray-800'}`}>
                    Question {i+1} {q.parseError && <span className="text-red-600">(⚠️ Needs Manual Input)</span>}
                  </div>
                  
                  {/* Uploading indicator */}
                  {uploadingImage[i] && (
                    <div className="mb-3 flex items-center gap-2 text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm">Uploading image to storage...</span>
                    </div>
                  )}
                  
                  {/* Image display area */}
                  {questionImages[i] && questionImages[i].length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {questionImages[i].map((imgSrc, imgIdx) => (
                        <div key={imgIdx} className="relative group">
                          <img 
                            src={imgSrc} 
                            alt={`Question ${i+1} attachment ${imgIdx+1}`}
                            className="max-w-xs max-h-48 object-contain border rounded shadow-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(i, imgIdx)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-2 rounded-r text-xs">
                      <div className="flex items-start">
                        <span className="text-lg mr-2">📸</span>
                        <div>
                          <p className="font-semibold text-blue-800 mb-1">💡 Tip: Paste Images for Complex Math</p>
                          <p className="text-blue-700">
                            For complex mathematical expressions (fractions, equations, diagrams), 
                            <strong> screenshot the original and paste it here</strong> using Ctrl+V (or Cmd+V on Mac). 
                            The image will be saved and shown to students.
                          </p>
                        </div>
                      </div>
                    </div>
                    <textarea
                      rows={3}
                      value={q.text}
                      onChange={e => updateQuestion(i, e.target.value)}
                      onPaste={e => handlePaste(e, i)}
                      placeholder="Enter the question text here... or paste an image"
                      className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 ${
                        q.parseError 
                          ? 'border-red-400 focus:ring-red-500 bg-white placeholder-red-400' 
                          : 'focus:ring-indigo-500'
                      }`}
                    />
                  </div>
                  <div className="mt-3">
                    <div className="font-semibold mb-2 text-gray-700">
                      Options {q.parseError && <span className="text-red-600 text-sm">(Edit each option)</span>}
                    </div>
                    {(q.options || []).map((opt, j) => (
                      <div key={j} className="flex items-center mb-2">
                        <span className="mr-2 font-semibold text-gray-600">{String.fromCharCode(65 + j)}.</span>
                        <input
                          type="text"
                          value={opt || ''}
                          onChange={e => updateOption(i, j, e.target.value)}
                          placeholder={`Enter option ${String.fromCharCode(65 + j)}...`}
                          className={`flex-1 border rounded px-2 py-1 focus:outline-none focus:ring-2 ${
                            q.parseError 
                              ? 'border-red-300 focus:ring-red-500 bg-white placeholder-red-300' 
                              : 'focus:ring-indigo-500'
                          }`}
                        />
                        <button type="button" onClick={() => removeOption(i, j)} className="ml-2 text-red-500 text-lg font-bold">×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addOption(i)}
                      className="text-indigo-600 hover:underline mt-2 font-semibold"
                    >
                      + Add Option
                    </button>
                  </div>
                  <div className="mt-4">
                    <label className="font-semibold text-gray-700">
                      Correct Answer {q.parseError && <span className="text-red-600 text-sm">(Select after editing options)</span>}
                    </label>
                    <select
                      value={q.answer}
                      onChange={e => updateAnswer(i, e.target.value)}
                      className={`w-full border rounded px-3 py-2 mt-2 focus:outline-none focus:ring-2 ${
                        q.parseError 
                          ? 'border-red-300 focus:ring-red-500 bg-white' 
                          : 'focus:ring-indigo-500'
                      }`}
                    >
                      <option value="">Select correct option</option>
                      {(q.options || []).map((opt, j) => <option key={j} value={opt || ''}>{opt || ''}</option>)}
                    </select>
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-4 justify-center mt-6">
                <button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl shadow font-bold transition"
                  disabled={courses.length === 0}
                  title={courses.length === 0 ? "You are not assigned to any course" : ""}
                >
                  Create Quiz
                </button>
              </div>
              {courses.length === 0 && (
                <div className="text-red-500 mt-2 text-center">
                  You are not assigned to any course. Please contact your administrator.
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default QuizUploadPage;
